import { useAui } from '@assistant-ui/react';

/**
 * The assistant-ui model-context tools map shape. Keyed by tool name; each
 * entry is the metadata bag the `<Tool>` component attached (`display`,
 * `message`, …).
 */
export type ToolsMap = Record<string, Record<string, unknown> | undefined> | undefined;

/**
 * Read the assistant-ui model-context tools map. Single source of truth
 * for the cast over the assistant-ui registry shape — every consumer that
 * needs to inspect attached tool-entry fields (the `<Tool>` component
 * attaches `display`, `message`, …) goes through this hook.
 *
 * Returns the raw map keyed by tool name; consumers narrow to the fields
 * they care about via {@link readToolField} or the typed wrappers in this
 * directory (`useToolEntryField`, `useToolDisplaySpec`,
 * `useIsCollapsibleTool`). Reach for this generic hook only when you need
 * the whole map (e.g. iterating many tool names per render).
 */
export function useToolsMap(): ToolsMap {
  const aui = useAui();
  return aui.modelContext().getModelContext().tools as ToolsMap;
}

/**
 * Look up a typed field on a tool entry by name. Pure helper over
 * {@link ToolsMap} — no hook, so consumers can call it inside loops.
 *
 * Returns `undefined` for missing tool, missing entry, or missing field.
 */
export function readToolField<T>(tools: ToolsMap, toolName: string | undefined, field: string): T | undefined {
  if (!toolName) {
    return undefined;
  }
  return tools?.[toolName]?.[field] as T | undefined;
}
